<template>
  <v-dialog
    transition="dialog-bottom-transition"
    v-model="isOpen"
    fullscreen
    persistent
    scrollable
    width="500"
  >
    <v-card flat>
      <v-card-title>
        <div>Batch Details: {{ selected_batch?.batch_number }}</div>
        <v-spacer></v-spacer>
        <v-btn
          color="red lighten-5 red--text"
          depressed
          rounded
          small
          @click="$emit('detailsClosed')"
        >
          Close
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row class="mb-5">
            <v-col cols="12" md="8">
              <v-card outlined>
                <v-card-title
                  class="d-flex align-center mb-8"
                  style="border-bottom: 1px solid #e0e0e0"
                >
                  Batch Overview
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <h4 class="text-subtitle-2">Batch Number</h4>
                      <p>{{ selected_batch?.batch_number }}</p>
                    </v-col>
                    <v-col cols="12" md="6">
                      <h4 class="text-subtitle-2">Item</h4>
                      <p>{{ selected_batch?.item?.name }}</p>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <h4 class="text-subtitle-2">Expiry Date</h4>
                      <p
                        v-if="
                          selected_batch?.item?.expires &&
                          selected_batch.expiry_date
                        "
                      >
                        {{ selected_batch.expiry_date | humanDate }}
                      </p>
                      <p v-else>N/A</p>
                    </v-col>
                    <v-col cols="12" md="6">
                      <h4 class="text-subtitle-2">Status</h4>
                      <v-avatar
                        :color="get_expiry_status(selected_batch).bgColor"
                        dark
                        size="35"
                      >
                        <v-icon
                          class="mr-1"
                          :color="get_expiry_status(selected_batch).color"
                          >{{ get_expiry_status(selected_batch).icon }}</v-icon
                        >
                      </v-avatar>
                      <span
                        :class="get_expiry_status(selected_batch).textColor"
                      >
                        {{ get_expiry_status(selected_batch).text }}
                      </span>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="4">
              <v-card outlined>
                <v-card-title
                  class="d-flex align-center mb-8"
                  style="border-bottom: 1px solid #e0e0e0"
                >
                  Utilization
                </v-card-title>
                <v-card-text class="text-center">
                  <v-progress-circular
                    :value="utilization"
                    size="100"
                    width="10"
                    :color="get_progress_color(selected_batch)"
                    >{{ utilization }}%</v-progress-circular
                  >
                  <p
                    class="text-caption mt-2"
                    v-if="selected_batch?.quantity_used === 0"
                  >
                    No units have been sold yet
                  </p>
                  <p class="text-caption mt-2" v-else>
                    {{ selected_batch?.quantity_used }} units have been sold
                  </p>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-card outlined class="mb-8">
            <v-card-title
              class="d-flex align-center mb-8"
              style="border-bottom: 1px solid #e0e0e0"
            >
              Quantity Information
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-card class="text-center" outlined>
                    <v-card-text class="mt-5">
                      <v-icon color="blue">mdi-package</v-icon>
                      <p class="text-subtitle-1">Initial Quantity</p>
                      <p class="text-h5 font-weight-bold">
                        {{ selected_batch?.quantity }}
                      </p>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card class="text-center" outlined>
                    <v-card-text class="mt-5">
                      <v-icon color="red">mdi-shopping</v-icon>
                      <p class="text-subtitle-1">Quantity Sold</p>
                      <p class="text-h5 font-weight-bold">
                        {{ selected_batch?.quantity_used }}
                      </p>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card class="text-center" outlined>
                    <v-card-text class="mt-5">
                      <v-icon color="green">mdi-check-circle</v-icon>
                      <p class="text-subtitle-1">Remaining Quantity</p>
                      <p class="text-h5 font-weight-bold">
                        {{
                          selected_batch?.quantity -
                          selected_batch?.quantity_used
                        }}
                      </p>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12">
                  <v-progress-linear
                    :value="utilization"
                    height="6"
                    :color="get_progress_color(selected_batch)"
                  ></v-progress-linear>
                  <div class="d-flex justify-space-between mt-2 text-caption">
                    <span> {{ selected_batch?.quantity_used }} </span>
                    <span
                      >{{
                        selected_batch?.quantity - selected_batch?.quantity_used
                      }}
                      in stock</span
                    >
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-5">
            <v-card-title
              class="d-flex align-center mb-8"
              style="border-bottom: 1px solid #e0e0e0"
            >
              Batch Transactions
            </v-card-title>
            <v-card-text>
              <v-expansion-panels multiple flat v-model="open_panels">
                <v-expansion-panel v-for="(item, index) in items" :key="index">
                  <v-expansion-panel-header :disabled="getting_batch_details">
                    <div class="d-flex align-center gap-3">
                      <v-avatar size="36" :color="item.color">
                        <v-icon :class="item.iconColor">{{ item.icon }}</v-icon>
                      </v-avatar>
                      <div>
                        <div class="font-weight-medium">{{ item.title }}</div>
                        <div class="text-caption text--secondary">
                          {{ item.description }}
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                    <v-skeleton-loader
                      type="table"
                      v-if="getting_batch_details"
                    ></v-skeleton-loader>
                    <template v-else>
                      <div
                        class="d-flex flex-column align-center justify-center py-8 text-center rounded-lg"
                        v-if="item.data.length === 0"
                        :class="{
                          'grey lighten-3': !$vuetify.theme.isDark,
                          black: $vuetify.theme.isDark,
                        }"
                      >
                        <v-avatar class="mb-4" size="64" color="grey lighten-2">
                          <v-icon class="grey--text">{{ item.icon }}</v-icon>
                        </v-avatar>
                        <h3 class="text-h6 font-weight-medium mb-2">
                          {{ item.emptyTitle }}
                        </h3>
                        <p class="text-caption text--secondary">
                          {{ item.emptyDescription }}
                        </p>
                      </div>

                      <div v-else>
                        <div v-if="item.key === 'stockAdjustments'">
                          <AssociatedStockAdjustments :items="item.data" />
                        </div>
                        <div v-else-if="item.key === 'sales'">
                          <AssociatedSales :items="item.data" />
                        </div>
                      </div>
                    </template>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import AssociatedStockAdjustments from "./AssociatedStockAdjustments.vue";
import AssociatedSales from "./AssociatedSales.vue";

export default {
  props: {
    detailsOpen: Boolean,
    selected_batch: Object,
  },
  components: {
    AssociatedStockAdjustments,
    AssociatedSales,
  },
  data() {
    return {
      activeTab: "details",
      open_panels: [0, 1, 2, 3],
      isOpen: false,
      getting_batch_details: false,
      items: [
        {
          title: "Associated Sales",
          key: "sales",
          description: "Sales transactions related to this batch",
          icon: "mdi-cart",
          iconColor: "green--text",
          color: "green lighten-5",
          emptyTitle: "No associated sales found",
          emptyDescription:
            "There are no sales associated with this batch yet.",
          buttonText: "Create New Sale",
          data: [],
          link: "/income/sales/new",
        },
        {
          title: "Associated Stock Adjustments",
          key: "stockAdjustments",
          description: "Inventory adjustments related to this batch",
          icon: "mdi-package-variant-closed",
          iconColor: "amber--text",
          color: "amber lighten-5",
          emptyTitle: "No associated stock adjustments found",
          emptyDescription:
            "There are no stock adjustments associated with this batch yet.",
          buttonText: "Create New Stock Adjustment",
          link: `/inventory`,
          data: [],
        },
      ],
    };
  },
  computed: {
    utilization() {
      return this.selected_batch?.quantity
        ? Math.round(
            (this.selected_batch.quantity_used / this.selected_batch.quantity) *
              100
          )
        : 0;
    },
    inventory_batch_settings() {
      return this.$store.state.user.user_infor.current_business
        .inventory_batch_setting;
    },
  },
  watch: {
    detailsOpen(val) {
      this.isOpen = val;
      if (val) {
        this.get_batch_details();
      }
    },
  },
  methods: {
    get_progress_color(item) {
      if (!item) return "green";
      const percentUsed = (item.quantity_used / item.quantity) * 100;
      if (percentUsed >= 90) return "red";
      if (percentUsed >= 50) return "amber";
      return "green";
    },
    getStatusColor(status) {
      if (status === "Safe") return "green";
      if (status === "Expiring Soon") return "orange";
      return "red";
    },
    get_expiry_status(item) {
      if (!item) {
        return {
          text: "Safe",
          color: "green",
          icon: "mdi-shield-check",
          bgColor: "green lighten-5",
          textColor: "green--text",
        };
      }
      const { expires, expiration_time_unit, expiration_time_value } =
        item.item;
      let time_unit = expiration_time_unit;
      let time_value = expiration_time_value;

      if (!time_unit && this.inventory_batch_settings) {
        time_unit = this.inventory_batch_settings.time_unit;
        time_value = this.inventory_batch_settings.time_value;
      }

      if (!expires || !time_unit || item.quantity - item.quantity_used === 0) {
        return {
          text: "Safe",
          color: "green",
          icon: "mdi-shield-check",
          bgColor: "green lighten-5",
          textColor: "green--text",
        };
      }

      const expiration_date = moment(item.expiry_date);
      const notice_date = expiration_date
        .clone()
        .subtract(time_value, time_unit);
      const today = moment();

      if (today.isSameOrAfter(expiration_date, "day")) {
        return {
          text: "Expired",
          color: "red",
          icon: "mdi-shield-remove-outline",
          bgColor: "red lighten-5",
          textColor: "red--text",
        };
      } else if (today.isSameOrAfter(notice_date, "day")) {
        return {
          text: "Expiring Soon",
          color: "amber",
          icon: "mdi-shield-half",
          bgColor: "orange lighten-5",
          textColor: "orange--text",
        };
      }
      return {
        text: "Safe",
        color: "green",
        icon: "mdi-shield-check",
        bgColor: "green lighten-5",
        textColor: "green--text",
      };
    },
    get_batch_details() {
      this.getting_batch_details = true;
      axios
        .get(`/api/item-batches/${this.selected_batch.id}`)
        .then((response) => {
          this.items = this.items.map((item) => {
            if (item.key === "stockAdjustments") {
              item.link = `/inventory/${this.selected_batch.item_id}/`;
              item.data = response.data.stock_adjustments;
            }
            if (item.key === "sales") {
              item.data = response.data.sales;
            }
            if (item.key === "invoices") {
              item.data = response.data.invoices;
            }
            if (item.key === "bills") {
              item.data = response.data.bills;
            }
            return item;
          });
        })
        .catch((error) => {})
        .finally(() => {
          this.getting_batch_details = false;
        });
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
</style>
